import type { Contract, JsonSchemaType } from '@gmn-portal/backend';
import { zodResolver } from '@hookform/resolvers/zod';
import type { TFunction } from 'i18next';
import {
  Building2,
  Calendar,
  CircleDot,
  Database,
  Edit3,
  FileText,
  Info,
  LayoutTemplate,
  Ship,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { useGlobalMetadata } from '@/core/api';
import type { PayloadApiError } from '@/core/api/client';
import { useObjects } from '@/core/api/objects';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { MultiSearchCombobox, SearchCombobox } from '@/shared/components/inputs';
import { DetailCard } from '@/shared/components/layout';
import { MetadataAutoForm } from '@/shared/components/metadata/metadata-auto-form';
import {
  MetadataBuilder,
  MetadataFormPreview,
} from '@/shared/components/metadata/metadata-builder';
import {
  Badge,
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui';
import {
  createMetadataZodSchema,
  parseMetadata,
  sanitizeMetadataValues,
} from '@/shared/utils/metadata';

import { createContractSchema } from '../schema/contract-schema';
import type { ContractFormData } from '../types/contract-form';

interface ContractFormProps {
  id?: string;
  defaultValues?: Partial<Contract>;
  onSubmit: (data: ContractFormData) => Promise<void>;
  error?: PayloadApiError;
  parentStartDate?: string;
}

// Error handling utilities
const createErrorMessageMapping = (t: TFunction<'compat'>) => ({
  StartDateAfterEndDate: {
    field: 'startDate' as const,
    message: t('validation.StartDateAfterEndDate'),
  },
  EndDateBeforeStartDate: {
    field: 'endDate' as const,
    message: t('validation.EndDateBeforeStartDate'),
  },
});

const handleValidationErrors = (
  errors: Array<{ message: string }>,
  form: UseFormReturn<ContractFormData, any, ContractFormData>,
  t: TFunction<'compat'>
) => {
  // Keep the current form values by resetting with them
  const currentValues = form.getValues();
  form.reset(currentValues, { keepValues: true });

  const errorMapping = createErrorMessageMapping(t);

  errors.forEach((err) => {
    const mapping = errorMapping[err.message as keyof typeof errorMapping];

    if (mapping) {
      if (Array.isArray(mapping)) {
        // Handle errors that affect multiple fields
        mapping.forEach(({ field, message }) => {
          form.setError(field, { message });
        });
      } else {
        // Handle single field errors
        form.setError(mapping.field, { message: mapping.message });
      }
    } else {
      // Fallback for unhandled errors
      toast.error(t('common.errorSaving'));
    }
  });
};

export function ContractForm({
  id,
  defaultValues,
  onSubmit,
  error,
  parentStartDate,
}: ContractFormProps) {
  const { t } = useIntl();
  const { user } = useAuth();
  const isGmnAdmin = user?.role === 'gmn-admin';

  const { data: globalMetadata } = useGlobalMetadata();

  const aggregatedObjectIds =
    (defaultValues?.aggregatedObjects as string[] | Array<{ id: string }>)?.map((obj) =>
      typeof obj === 'string' ? obj : obj.id
    ) || [];
  const { data: objectsData, isLoading: isLoadingObjects } = useObjects({
    where: {
      id: {
        in: aggregatedObjectIds,
      },
    },
    limit: aggregatedObjectIds.length,
    disabled: aggregatedObjectIds.length === 0,
  });

  const [metadataSchema, setMetadataSchema] = useState<z.ZodObject<z.ZodRawShape>>(z.object({}));
  const [isMetadataDrawerOpen, setIsMetadataDrawerOpen] = useState(false);
  const [dynamicMetadataSchema, setDynamicMetadataSchema] = useState<z.ZodObject<z.ZodRawShape>>(
    z.object({})
  );
  // Temporary state for the drawer - only applied when saving
  const [drawerSchema, setDrawerSchema] = useState<JsonSchemaType | undefined>(undefined);

  const initialFormValues: ContractFormData = {
    contractNumber: defaultValues?.contractNumber || '',
    title: defaultValues?.title || '',
    customer:
      typeof defaultValues?.customer === 'string'
        ? defaultValues.customer
        : defaultValues?.customer?.id || '',
    objects:
      (defaultValues?.objects as ({ id: string } | string)[])?.map((obj) =>
        typeof obj === 'string' ? obj : obj.id
      ) || [],
    startDate: defaultValues?.startDate || '',
    endDate: defaultValues?.endDate || '',
    description: defaultValues?.description || '',
    status: (defaultValues?.status as ContractFormData['status']) || 'open',
    metadata: parseMetadata(defaultValues?.metadata as Record<string, unknown>),
    dynamicMetadataSchema: (defaultValues?.dynamicMetadataSchema as JsonSchemaType) || undefined,
    dynamicMetadataValues: parseMetadata(
      defaultValues?.dynamicMetadataValues as Record<string, unknown>
    ),
  };

  const form = useForm<ContractFormData, any, ContractFormData>({
    resolver: zodResolver(createContractSchema(t, metadataSchema, dynamicMetadataSchema)),
    defaultValues: initialFormValues,
  });

  // Update form values when defaultValues changes (e.g. when React Query refetches)
  useEffect(() => {
    // Only reset form if we're not in an error state and the values have actually changed
    if (defaultValues && !error && !form.formState.isDirty) {
      form.reset({
        contractNumber: defaultValues.contractNumber || '',
        title: defaultValues.title || '',
        customer:
          typeof defaultValues?.customer === 'string'
            ? defaultValues.customer
            : defaultValues?.customer?.id || '',
        objects:
          (defaultValues.objects as ({ id: string } | string)[])?.map((obj) =>
            typeof obj === 'string' ? obj : obj.id
          ) || [],
        startDate: defaultValues.startDate || '',
        endDate: defaultValues.endDate || '',
        description: defaultValues.description || '',
        status: defaultValues.status || 'open',
        metadata: parseMetadata(defaultValues.metadata as Record<string, unknown>),
        dynamicMetadataSchema: (defaultValues.dynamicMetadataSchema as JsonSchemaType) || undefined,
        dynamicMetadataValues: parseMetadata(
          defaultValues.dynamicMetadataValues as Record<string, unknown>
        ),
      });
    }
  }, [defaultValues, form, error]);

  useEffect(() => {
    if (globalMetadata) {
      const metadataConfig = globalMetadata.rootContractMetadata as JsonSchemaType | undefined;
      if (metadataConfig) {
        setMetadataSchema(createMetadataZodSchema(metadataConfig, isGmnAdmin));
      }
    }
  }, [globalMetadata, isGmnAdmin]);

  useEffect(() => {
    if (defaultValues?.dynamicMetadataSchema) {
      setDynamicMetadataSchema(
        createMetadataZodSchema(defaultValues.dynamicMetadataSchema as JsonSchemaType, isGmnAdmin)
      );
    }
  }, [defaultValues?.dynamicMetadataSchema, isGmnAdmin]);

  // Sanitize metadata values when schema changes (handles type conversions like string->number, etc.)
  useEffect(() => {
    // Sanitize static metadata
    if (globalMetadata) {
      const staticSchema = globalMetadata.rootContractMetadata as JsonSchemaType | undefined;
      const currentMeta = (form.getValues().metadata || {}) as Record<string, unknown>;
      const sanitizedMeta = sanitizeMetadataValues(currentMeta, staticSchema);
      if (JSON.stringify(currentMeta) !== JSON.stringify(sanitizedMeta)) {
        form.setValue('metadata', sanitizedMeta, { shouldValidate: false });
      }
    }

    // Sanitize dynamic metadata
    const currentDynValues = (form.getValues().dynamicMetadataValues || {}) as Record<
      string,
      unknown
    >;
    const sanitizedDyn = sanitizeMetadataValues(
      currentDynValues,
      form.getValues().dynamicMetadataSchema as JsonSchemaType
    );
    if (JSON.stringify(currentDynValues) !== JSON.stringify(sanitizedDyn)) {
      form.setValue('dynamicMetadataValues', sanitizedDyn, {
        shouldValidate: false,
      });
    }
  }, [globalMetadata, form, metadataSchema, dynamicMetadataSchema]);

  // Initialize drawer schema when opening
  const openMetadataDrawer = () => {
    setDrawerSchema(form.getValues().dynamicMetadataSchema);
    setIsMetadataDrawerOpen(true);
  };

  const handleMetadataBuilderSave = () => {
    if (drawerSchema) {
      form.setValue('dynamicMetadataSchema', drawerSchema);
      setDynamicMetadataSchema(createMetadataZodSchema(drawerSchema, isGmnAdmin));
    }
    setIsMetadataDrawerOpen(false);
  };

  // Handle backend validation errors
  useEffect(() => {
    if (error?.response?.errors) {
      handleValidationErrors(error.response.errors as Array<{ message: string }>, form, t);
    }
  }, [error, form, t]);

  const handleSubmit = async (data: ContractFormData) => {
    await onSubmit(data);
  };

  return (
    <TooltipProvider>
      <Form {...form}>
        <form id={id} onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="grid gap-6">
            <div className="grid grid-cols-12 gap-6">
              {/* Main Details Card */}
              <div className="col-span-9 space-y-6">
                <DetailCard icon={FileText} title={t('contracts.contractDetails')}>
                  <p className="text-sm text-muted-foreground mb-6">
                    {t('contracts.contractDetailsDescription')}
                  </p>
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel required>{t('contracts.title')}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="contractNumber"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('contracts.contractNumber')}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('contracts.description')}</FormLabel>
                          <FormControl>
                            <Textarea {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </DetailCard>

                {/* Predefined Metadata Card */}
                <DetailCard icon={LayoutTemplate} title={t('metadata.predefinedMetadata')}>
                  <p className="text-sm text-muted-foreground mb-6">
                    {t('metadata.predefinedMetadataDescription')}
                  </p>
                  {Object.keys(metadataSchema.shape || {}).length > 0 ? (
                    <MetadataAutoForm
                      schema={metadataSchema}
                      form={form}
                      path={['metadata']}
                      metadataSchema={
                        globalMetadata?.rootContractMetadata as JsonSchemaType | undefined
                      }
                    />
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      ({t('metadata.noContractMetadata')})
                    </div>
                  )}
                </DetailCard>

                {/* Dynamic Metadata Card */}
                <DetailCard
                  icon={Database}
                  title={t('metadata.customMetadata')}
                  headerAction={
                    isGmnAdmin ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto"
                        onClick={openMetadataDrawer}
                        type="button"
                      >
                        <Edit3 className="size-3!" />
                        {t('metadata.addCustomMetadata')}
                      </Button>
                    ) : undefined
                  }
                >
                  <p className="text-sm text-muted-foreground mb-6">
                    {t('metadata.customMetadataBuilderDescription')}
                  </p>
                  {form.watch('dynamicMetadataSchema') &&
                  Object.keys(form.watch('dynamicMetadataSchema.properties') || {}).length > 0 ? (
                    <MetadataAutoForm
                      schema={dynamicMetadataSchema}
                      form={form}
                      path={['dynamicMetadataValues']}
                      metadataSchema={form.watch('dynamicMetadataSchema') as JsonSchemaType}
                    />
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      ({t('metadata.noMetadata')})
                    </div>
                  )}
                </DetailCard>
              </div>

              {/* Right Column */}
              <div className="col-span-3 space-y-6">
                {/* Status Card */}
                <DetailCard icon={CircleDot} title={t('contracts.statusLabel')}>
                  <p className="text-sm text-muted-foreground mb-6">
                    {t('contracts.statusDescription')}
                  </p>
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('contracts.statusLabel')}</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value || 'open'}
                            onValueChange={field.onChange}
                            disabled={!isGmnAdmin}
                          >
                            <SelectTrigger>
                              <div className="flex items-center">
                                <CircleDot className="size-4 mr-2 text-muted-foreground" />
                                <SelectValue />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">{t('contracts.status.open')}</SelectItem>
                              <SelectItem value="closed">{t('contracts.status.closed')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </DetailCard>

                <DetailCard icon={Building2} title={t('contracts.customerAssignment')}>
                  <p className="text-sm text-muted-foreground mb-6">
                    {t('contracts.customerAssignmentDescription')}
                  </p>
                  <div className="space-y-2">
                    <FormLabel required>{t('customers.customer')}</FormLabel>
                    <FormField
                      control={form.control}
                      name="customer"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <SearchCombobox
                              value={field.value || undefined}
                              onSelect={field.onChange}
                              collectionKey="customers"
                              searchKey="name"
                              disabled={!isGmnAdmin}
                              texts={{
                                selectItemMsg: t('contracts.selectCustomer'),
                                searchPlaceholder: t('contracts.searchCustomer'),
                              }}
                              icon={Building2}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </DetailCard>

                {/* Object Assignment Card */}
                <DetailCard icon={Ship} title={t('contracts.objectsAssignment')}>
                  <p className="text-sm text-muted-foreground mb-6">
                    {t('contracts.objectsAssignmentDescription')}
                  </p>
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <FormLabel>{t('contracts.directlyAssignedObjects')}</FormLabel>
                      <FormField
                        control={form.control}
                        name="objects"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <MultiSearchCombobox
                                value={field.value || []}
                                onSelect={field.onChange}
                                collectionKey="objects"
                                searchKey="name"
                                texts={{
                                  selectItemMsg: t('contracts.selectObjects'),
                                  searchPlaceholder: t('contracts.searchObject'),
                                }}
                                icon={Ship}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Read-only display of aggregated objects */}
                    {aggregatedObjectIds.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <FormLabel>{t('contracts.aggregatedObjects')}</FormLabel>
                          <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                              <Info className="size-4 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs">
                                {t('contracts.aggregatedObjectsDescription')}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <div className="space-y-2">
                          {isLoadingObjects ? (
                            <div className="flex flex-wrap gap-2">
                              {aggregatedObjectIds.map((_, index) => (
                                <Skeleton key={index} className="h-[22px] w-20" />
                              ))}
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {(objectsData?.docs || []).map((obj) => (
                                <Badge key={obj.id} variant="secondary">
                                  {obj.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </DetailCard>

                {/* Dates Card */}
                <DetailCard icon={Calendar} title={t('contracts.contractDetails')}>
                  <p className="text-sm text-muted-foreground mb-6">
                    {t('contracts.contractDatesDescription')}
                  </p>
                  <div className="space-y-6">
                    <FormField
                      control={form.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('contracts.startDate')}</FormLabel>
                          <FormControl>
                            <DatePicker
                              date={field.value ? new Date(field.value) : undefined}
                              setDate={(date) => field.onChange(date?.toISOString() || '')}
                              placeholder={t('contracts.chooseStartDate')}
                              defaultMonth={
                                field.value
                                  ? new Date(field.value)
                                  : parentStartDate
                                    ? new Date(parentStartDate)
                                    : undefined
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('contracts.endDate')}</FormLabel>
                          <FormControl>
                            <DatePicker
                              date={field.value ? new Date(field.value) : undefined}
                              setDate={(date) => field.onChange(date?.toISOString() || '')}
                              placeholder={t('contracts.chooseEndDate')}
                              defaultMonth={
                                field.value
                                  ? new Date(field.value)
                                  : parentStartDate
                                    ? new Date(parentStartDate)
                                    : undefined
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </DetailCard>
              </div>
            </div>
          </div>
        </form>
      </Form>
      <Dialog open={isMetadataDrawerOpen} onOpenChange={setIsMetadataDrawerOpen}>
        <DialogContent
          className="!max-w-[90vw] !w-[1400px] max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0"
          showCloseButton={false}
        >
          <div className="flex-1 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 h-[70vh]">
              <MetadataBuilder
                value={drawerSchema}
                onChange={setDrawerSchema}
                className="border-0 rounded-none shadow-none lg:border-r h-full"
                header={false}
              />
              <MetadataFormPreview
                schema={drawerSchema}
                className="border-0 rounded-none shadow-none hidden lg:flex h-full"
                header={false}
              />
            </div>
          </div>
          <DialogFooter className="border-t p-5">
            <Button type="button" variant="outline" onClick={() => setIsMetadataDrawerOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={handleMetadataBuilderSave}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
